import React, { lazy, Suspense, useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Filter,
  FlaskConical,
  MonitorStop,
  Building2,
  Settings,
  Contact2,
  Beaker,
  Pill,
  ClipboardList,
  ClipboardSignature,
  ClipboardCheck,
  Component,
  FileSpreadsheet,
  FileText,
  CalendarPlus,
  // CalendarCheck2,
  BookOpen,
  ChevronRight,
  LogOut,
  ExternalLink,
  Search,
  History,
  Wrench,
  UserCircle2,
  // FileSearch,
  Bug,
  Bot,
  MessageSquare,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { usePermissions } from "@/context/PermissionsContext";
import { ScrollArea } from "@/Components/ui/scroll-area";
import { Separator } from "@/Components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/Components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/Components/ui/collapsible";

import LogoImage from "../../Assets/SUNRIDGELogo 1.png";

const ScheduleTreatmentModal = lazy(() => import("../ScheduleTreatmentModal"));
const RulesToFollowModal = lazy(() => import("../RulesToFollowModal"));

// Maps nav item name → permissions key (null = always visible)
const NAV_PERMISSION_MAP = {
  todayview: 'todayView',
  // 'daily-command-center': 'todayView',
  // 'document-intelligence': 'documents',
  filters: 'filters',
  lab: 'lab',
  emr: 'emr',
  crm: 'crm',
  'patient-forms': 'patientForms',
  'front desk': 'frontDesk',
  admin: 'admin',
  'task-management': 'taskManagement',
  'document-intelligence': 'documentIntelligence',
  'daily-command-center': 'dailyCommandCenter',
  'admin logs': 'adminLogs',
  // Old Views use separate permission keys
  todayviewold: 'todayViewOld',
  'n.treatment': 'treatmentViewOld',
  treatment: 'treatmentViewOld',
  prep: 'auditViewOld',
  'emr-old': 'emrOld',
  // Dev views
  emrv2: 'emrV2',
  'n.patientform': 'patientForms',
  room: 'roomView',
  'export data': 'exportData',
  'patient form': 'patientForms',
  'patient summary': 'patientSummary',
  schedule: 'schedule',
  rulesToFollow: 'rulesToFollow',
  contacts: 'contacts',
};

// Navigation data
const mainNavLinks = [
  { name: "todayview", displayName: "Today", path: "/todayview", icon: LayoutDashboard, description: "Daily flow" },
  // { name: "daily-command-center", displayName: "Command", path: "/daily-command-center", icon: CalendarCheck2, description: "Attention queue" },
  // { name: "document-intelligence", displayName: "Documents AI", path: "/document-intelligence", icon: FileSearch, description: "Fax and form triage" },
  { name: "emr", displayName: "EMR", path: "/emr", icon: MonitorStop, description: "Patient charts" },
  { name: "front desk", displayName: "Front Desk", path: "/frontdesk", icon: Building2, description: "Arrivals and rooms" },
  { name: "lab", displayName: "Lab", path: "/labtracker", icon: FlaskConical, description: "Orders and results" },
  { name: "filters", displayName: "Filters", path: "/filters", icon: Filter, description: "Find treatments" },
  { name: "crm", displayName: "CRM", path: "/crm", icon: Contact2, description: "Leads and outreach" },
  { name: "patient-forms", displayName: "Patient Forms", path: "/patient-forms", icon: ClipboardSignature, description: "Intake and consent" },
  { name: "task-management", displayName: "Tasks", path: "/task-management", icon: ClipboardCheck, description: "Team work queue" },
  { name: "admin", displayName: "Admin", path: "/updations", icon: Settings, description: "Configuration" },
  { name: "admin logs", displayName: "Logs", path: "/admin-logs", icon: History, description: "Audit trail" },
];

const oldViewsNavLinks = [
  { name: "todayviewold", displayName: "Today (Old)", path: "/todayviewold", icon: LayoutDashboard },
  { name: "n.treatment", displayName: "Treatment (New)", path: "/treatmentview", icon: Beaker },
  { name: "treatment", displayName: "Treatment (Old)", path: "/treatmentviewdisplay", icon: Pill },
  { name: "prep", displayName: "Prep", path: "/auditview", icon: ClipboardList },
  { name: "emr-old", displayName: "EMR (Old)", path: "/old-emr", icon: MonitorStop },
];

const devNavLinks = [
  { name: "n.patientform", displayName: "Patient Form", path: "/patientform/preview", icon: ClipboardSignature },
  { name: "room", displayName: "Room View", path: "/roomview", icon: Component },
  { name: "export data", displayName: "Export Data", path: "/exportdata", icon: FileSpreadsheet },
  { name: "patient form", displayName: "Patient Form Old", path: "/patientformold", icon: ClipboardSignature },
  { name: "patient summary", displayName: "Patient Summary", path: "/patientsummary", icon: FileText },
];

const sidebarTooltipClass = "bg-[#171b20] border-white/10 text-white shadow-xl";

const SectionLabel = ({ icon: Icon, children, isCollapsed }) => {
  if (isCollapsed) {
    return (
      <div className="my-2 flex justify-center">
        <div className="h-px w-8 bg-white/10" />
      </div>
    );
  }

  return (
    <div className="mb-1 mt-4 flex items-center gap-2 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
      {Icon ? <Icon size={13} strokeWidth={1.8} className="text-slate-500" /> : null}
      <span>{children}</span>
    </div>
  );
};

// NavItem Component
const NavItem = ({ item, isCollapsed, isActive, onClick }) => {
  const Icon = item.icon;
  const isExternal = item.external;

  const content = (
    <div
      className={cn(
        "group relative flex min-h-[44px] items-center gap-3 rounded-xl px-2.5 py-2 transition-all duration-200",
        "outline-none ring-offset-0 focus-visible:ring-2 focus-visible:ring-sunridge-cyan/40",
        isCollapsed && "justify-center px-0",
        isActive
          ? "bg-white text-slate-950 shadow-sm shadow-black/20"
          : "text-slate-300 hover:bg-white/[0.07] hover:text-white"
      )}
    >
      {/* Active indicator bar */}
      <div
        className={cn(
          "absolute left-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-full transition-all duration-300",
          "bg-gradient-to-b from-sunridge-cyan via-sunridge-teal to-sunridge-lime",
          isActive ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"
        )}
      />

      {/* Icon */}
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200",
          isActive
            ? "bg-sunridge-cyan/10 text-sunridge-cyan"
            : "text-slate-400 group-hover:bg-white/[0.06] group-hover:text-white"
        )}
      >
        <Icon
          size={19}
          strokeWidth={1.75}
          className={cn(
            "transition-all duration-200",
            isActive && "drop-shadow-[0_0_8px_rgba(31,160,203,0.5)]"
          )}
        />
      </div>

      {/* Label */}
      {!isCollapsed && (
        <div className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate text-sm font-semibold leading-5 transition-colors duration-200",
              isActive ? "text-slate-950" : "text-slate-200 group-hover:text-white"
            )}
          >
            {item.displayName}
          </span>
          {item.description ? (
            <span
              className={cn(
                "block truncate text-[11px] leading-4 transition-colors duration-200",
                isActive ? "text-slate-500" : "text-slate-500 group-hover:text-slate-400"
              )}
            >
              {item.description}
            </span>
          ) : null}
        </div>
      )}

      {/* External link indicator */}
      {isExternal && !isCollapsed && (
        <ExternalLink size={12} className="ml-auto text-slate-500" />
      )}
    </div>
  );

  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          {isExternal ? (
            <a
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClick}
            >
              {content}
            </a>
          ) : (
            <NavLink to={item.path} onClick={onClick}>
              {content}
            </NavLink>
          )}
        </TooltipTrigger>
        <TooltipContent side="right" className={sidebarTooltipClass}>
          {item.displayName}
        </TooltipContent>
      </Tooltip>
    );
  }

  return isExternal ? (
    <a href={item.path} target="_blank" rel="noopener noreferrer" onClick={onClick}>
      {content}
    </a>
  ) : (
    <NavLink to={item.path} onClick={onClick}>
      {content}
    </NavLink>
  );
};

// Portal AI assistant — self-contained menu entry (easy to relocate).
// Mirrors NavItem styling but acts as a button that opens the assistant overlay.
const AssistantMenuItem = ({ isCollapsed, onClick }) => {
  const content = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex min-h-[44px] w-full items-center gap-3 rounded-xl px-2.5 py-2 transition-all duration-200",
        "outline-none ring-offset-0 focus-visible:ring-2 focus-visible:ring-sunridge-cyan/40",
        "text-slate-300 hover:bg-white/[0.07] hover:text-white",
        isCollapsed && "justify-center px-0"
      )}
    >
      {/* Icon */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-all duration-200 group-hover:bg-white/[0.06] group-hover:text-white">
        <span className="relative inline-flex h-[19px] w-[19px] shrink-0 items-center justify-center">
          <Bot size={19} strokeWidth={1.75} className="transition-all duration-200" />
          <MessageSquare
            size={11}
            strokeWidth={1.75}
            className="absolute -bottom-1 -right-1 rounded-full bg-[#171b20] text-current"
          />
        </span>
      </div>

      {/* Label */}
      {!isCollapsed && (
        <div className="min-w-0 flex-1 text-left">
          <span className="block truncate text-sm font-semibold leading-5 text-slate-200 transition-colors duration-200 group-hover:text-white">
            AI Assistant
          </span>
          <span className="block truncate text-[11px] leading-4 text-slate-500 transition-colors duration-200 group-hover:text-slate-400">
            Ask the portal
          </span>
        </div>
      )}
    </button>
  );

  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className={sidebarTooltipClass}>
          AI Assistant
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
};

const SidebarActionButton = ({ icon: Icon, label, tone = "green", isCollapsed, onClick }) => {
  const toneClasses = {
    green: "border-sunridge-green/25 bg-sunridge-green/10 text-sunridge-lime hover:bg-sunridge-green/20 hover:border-sunridge-green/40",
    cyan: "border-sunridge-cyan/25 bg-sunridge-cyan/10 text-sunridge-cyan hover:bg-sunridge-cyan/20 hover:border-sunridge-cyan/40",
    teal: "border-sunridge-teal/25 bg-sunridge-teal/10 text-sunridge-teal hover:bg-sunridge-teal/20 hover:border-sunridge-teal/40",
    amber: "border-amber-300/30 bg-amber-300/10 text-amber-200 hover:bg-amber-300/20 hover:border-amber-300/50",
  };

  const content = (
    <button
      onClick={onClick}
      className={cn(
        "group inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border px-2 py-2",
        "font-semibold transition-all duration-200 active:scale-[0.98]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sunridge-cyan/40",
        toneClasses[tone],
        isCollapsed ? "w-full px-0" : "w-full"
      )}
    >
      <Icon size={18} strokeWidth={2} className="shrink-0" />
      {!isCollapsed ? <span className="truncate text-sm text-white">{label}</span> : null}
    </button>
  );

  if (!isCollapsed) return content;

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="right" className={sidebarTooltipClass}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({
  title,
  icon: Icon,
  items,
  isCollapsed,
  activeLink,
  onLinkClick,
  forceOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasActiveItem = items.some((item) => item.name === activeLink);

  useEffect(() => {
    if (hasActiveItem || forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen, hasActiveItem]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            "flex w-full items-center gap-3 rounded-xl px-2.5 py-2 transition-all duration-200",
            "text-slate-400 hover:bg-white/[0.06] hover:text-white",
            isCollapsed && "justify-center px-0"
          )}
          aria-expanded={isOpen}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.03]">
            {Icon && !isCollapsed ? (
              <Icon size={16} strokeWidth={1.75} />
            ) : (
              <ChevronRight
                size={16}
                className={cn("transition-transform duration-200", isOpen && "rotate-90")}
              />
            )}
          </div>
          {!isCollapsed && (
            <span className="flex-1 text-left text-xs font-semibold uppercase tracking-[0.16em]">
              {title}
            </span>
          )}
          {!isCollapsed ? (
            <ChevronRight
              size={15}
              className={cn("transition-transform duration-200", isOpen && "rotate-90")}
            />
          ) : null}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
        <div className={cn("mt-1 space-y-1", isCollapsed ? "" : "pl-2")}>
          {items.map((item) => (
            <NavItem
              key={item.name}
              item={item}
              isCollapsed={isCollapsed}
              isActive={activeLink === item.name}
              onClick={() => onLinkClick(item.name)}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Main SidebarNav Component
export function SidebarNav({ isCollapsed = false, onNavigate, hasCloseControl = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPageAccess, permissionsLoaded } = usePermissions();
  const [activeLink, setActiveLink] = useState(
    localStorage.getItem("activeLink") || "todayview"
  );
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const userName = useMemo(() => localStorage.getItem("userShowName") || "Signed-in user", []);
  const userType = useMemo(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return "User";
    return storedUser.charAt(0).toUpperCase() + storedUser.slice(1);
  }, []);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filterLinks = (items) => {
    const allowedItems = items.filter((item) => {
      if (!permissionsLoaded) return true;
      const permissionKey = NAV_PERMISSION_MAP[item.name] || NAV_PERMISSION_MAP[item.path?.replace('/', '')];
      return !permissionKey || hasPageAccess(permissionKey);
    });

    if (!normalizedSearch) return allowedItems;
    return allowedItems.filter((item) => {
      const searchableText = [
        item.displayName,
        item.name,
        item.description,
        item.path,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(normalizedSearch);
    });
  };

  const visibleMainNavLinks = useMemo(
    () => filterLinks(mainNavLinks),
    [normalizedSearch, permissionsLoaded, hasPageAccess]
  );
  const visibleOldViewsNavLinks = useMemo(
    () => filterLinks(oldViewsNavLinks),
    [normalizedSearch, permissionsLoaded, hasPageAccess]
  );
  const visibleDevNavLinks = useMemo(
    () => filterLinks(devNavLinks),
    [normalizedSearch, permissionsLoaded, hasPageAccess]
  );
  const hasSearchResults =
    visibleMainNavLinks.length + visibleOldViewsNavLinks.length + visibleDevNavLinks.length > 0;

  // Update active link based on location
  useEffect(() => {
    if (location.pathname) {
      const allLinks = [...mainNavLinks, ...oldViewsNavLinks, ...devNavLinks];

      // Handle root path "/" which should map to todayview
      const pathToMatch = location.pathname === "/"
        ? "/todayview"
        : `/${location.pathname.split("/")[1]}`;

      const foundNavLink = allLinks.find(
        (navLink) => navLink.path === pathToMatch
      );

      if (foundNavLink) {
        setActiveLink(foundNavLink.name);
        localStorage.setItem("activeLink", foundNavLink.name);
      }
    }
  }, [location.pathname]);


  const handleLinkClick = (link) => {
    setActiveLink(link);
    localStorage.setItem("activeLink", link);
    onNavigate?.();
  };

  const handleSignOut = () => {
    localStorage.clear();
    navigate("/login");
    window.location.reload();
  };

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false);
  };

  const openBugReporter = () => {
    window.dispatchEvent(new CustomEvent("sunridge:open-bug-report"));
    onNavigate?.();
  };

  const openPortalAssistant = () => {
    window.dispatchEvent(new CustomEvent("sunridge:open-portal-assistant"));
    onNavigate?.();
  };

  return (
    <TooltipProvider>
      <div className="flex h-full flex-col text-slate-200">
        {/* Brand */}
        <div
          className={cn(
            "px-4 pb-4 pt-5",
            isCollapsed && "px-3 pb-3 pt-4",
            !isCollapsed && hasCloseControl && "pr-16"
          )}
        >
          {!isCollapsed ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3">
              <div className="flex min-h-11 items-center">
                <img
                  src={LogoImage}
                  alt="SunRidge Medical"
                  className="h-8 w-auto max-w-full object-contain transition-opacity duration-300"
                />
              </div>
              <p className="m-0 mt-2 truncate text-[11px] font-medium leading-4 text-slate-500">
                Clinical Portal
              </p>
            </div>
          ) : (
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05]">
              <span className="text-base font-bold text-sunridge-cyan">S</span>
            </div>
          )}
        </div>

        {!isCollapsed ? (
          <div className="px-4 pb-3">
            <div className="relative">
              <Search
                size={15}
                strokeWidth={1.8}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search navigation"
                className={cn(
                  "h-10 w-full rounded-xl border border-white/10 bg-black/10 pl-9 pr-3",
                  "text-sm font-medium text-slate-100 placeholder:text-slate-500",
                  "outline-none transition focus:border-sunridge-cyan/40 focus:bg-black/20 focus:ring-2 focus:ring-sunridge-cyan/20"
                )}
              />
            </div>
          </div>
        ) : null}

        <Separator className="mx-4 bg-white/10" />

        {/* Navigation */}
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 scrollbar-none">
          <nav className="space-y-1">
            {/* Main Navigation */}
            <SectionLabel icon={LayoutDashboard} isCollapsed={isCollapsed}>
              Workspace
            </SectionLabel>
            {/* Portal AI assistant — self-contained; relocate as one block if needed */}
            {!normalizedSearch ? (
              <AssistantMenuItem
                isCollapsed={isCollapsed}
                onClick={openPortalAssistant}
              />
            ) : null}
            {visibleMainNavLinks.map((item) => (
              <NavItem
                key={item.name}
                item={item}
                isCollapsed={isCollapsed}
                isActive={activeLink === item.name}
                onClick={() => handleLinkClick(item.name)}
              />
            ))}

            {!isCollapsed && !hasSearchResults ? (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-4 text-center text-sm font-medium text-slate-500">
                {normalizedSearch
                  ? `No pages match “${searchQuery.trim()}”`
                  : 'No pages are assigned to your account'}
              </div>
            ) : null}

            {!normalizedSearch ? (
              <>
                <SectionLabel icon={CalendarPlus} isCollapsed={isCollapsed}>
                  Quick Actions
                </SectionLabel>
                <div className={cn("grid gap-2", isCollapsed ? "grid-cols-1" : "grid-cols-3")}>
                  {hasPageAccess('schedule') && (
                    <SidebarActionButton
                      icon={CalendarPlus}
                      label="Schedule"
                      tone="green"
                      isCollapsed={isCollapsed}
                      onClick={() => setIsScheduleModalOpen(true)}
                    />
                  )}
                  {hasPageAccess('rulesToFollow') && (
                    <SidebarActionButton
                      icon={BookOpen}
                      label="SOPs"
                      tone="cyan"
                      isCollapsed={isCollapsed}
                      onClick={() => setIsRulesModalOpen(true)}
                    />
                  )}
                  <SidebarActionButton
                    icon={Bug}
                    label="Bug"
                    tone="amber"
                    isCollapsed={isCollapsed}
                    onClick={openBugReporter}
                  />
                </div>
              </>
            ) : null}

            {/* Old Views Section */}
            {visibleOldViewsNavLinks.length > 0 ? (
              <CollapsibleSection
                title="Old Views"
                icon={History}
                items={visibleOldViewsNavLinks}
                isCollapsed={isCollapsed}
                activeLink={activeLink}
                onLinkClick={handleLinkClick}
                forceOpen={Boolean(normalizedSearch)}
              />
            ) : null}

            {/* Development Section */}
            {visibleDevNavLinks.length > 0 ? (
              <CollapsibleSection
                title="Development"
                icon={Wrench}
                items={visibleDevNavLinks}
                isCollapsed={isCollapsed}
                activeLink={activeLink}
                onLinkClick={handleLinkClick}
                forceOpen={Boolean(normalizedSearch)}
              />
            ) : null}
          </nav>
        </div>

        <Separator className="mx-4 bg-white/10" />

        {/* Sign Out Button */}
        <div className={cn("p-3", !isCollapsed && "p-4")}>
          {!isCollapsed ? (
            <div className="mb-3 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sunridge-cyan/10 text-sunridge-cyan">
                <UserCircle2 size={19} strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <p className="m-0 truncate text-sm font-semibold leading-5 text-white">
                  {userName}
                </p>
                <p className="m-0 truncate text-[11px] font-medium leading-4 text-slate-500">
                  {userType}
                </p>
              </div>
            </div>
          ) : null}
          {isCollapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={handleSignOut}
                  className={cn(
                    "flex w-full items-center justify-center rounded-xl p-2.5",
                    "bg-white/[0.04] text-slate-400 hover:bg-red-500/20 hover:text-red-300",
                    "transition-all duration-200"
                  )}
                >
                  <LogOut size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className={sidebarTooltipClass}>
                Sign Out
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={handleSignOut}
              className={cn(
                "flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5",
                "bg-white/[0.04] text-slate-400 hover:bg-red-500/20 hover:text-red-300",
                "transition-all duration-200 font-medium"
              )}
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          )}
        </div>

        <Suspense fallback={null}>
          {/* Schedule Modal */}
          {isScheduleModalOpen && (
            <ScheduleTreatmentModal
              isOpen={isScheduleModalOpen}
              closeModal={closeScheduleModal}
            />
          )}

          {/* Rules To Follow Modal */}
          {isRulesModalOpen && (
            <RulesToFollowModal
              isOpen={isRulesModalOpen}
              onClose={() => setIsRulesModalOpen(false)}
            />
          )}
        </Suspense>
      </div>
    </TooltipProvider>
  );
}

export default SidebarNav;
